import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Lock } from 'lucide-react';
import { Mascot } from '@/components/Mascot';
import { supabase } from '@/lib/supabase';
import { useUserStore } from '@/store/userStore';
import { NORMS, STYLE_STEPS, DISCLAIMER, type EatingScale } from '@/lib/eatingProfile';
import { buildPortrait, situations } from '@/lib/dofiStart';
import { isPaidNow } from '@/lib/promo';

/**
 * Расширенный портрет пищевого поведения — бонус к подписке (30.10.2026).
 * Всё считает код: баллы теста против норм, ситуации из ответов, ритм
 * по дневнику за 14 дней. Модели нет. Без подписки — заголовок и замок.
 */
const SCALE: Record<EatingScale, { name: string; what: string }> = {
  emotional: { name: 'Эмоциональная сторона', what: 'Насколько еда помогает справляться с чувствами: напряжением, скукой, усталостью.' },
  external: { name: 'Внешняя сторона', what: 'Насколько тебя зовёт сама еда: вид, запах, компания, то, что под рукой.' },
  restrained: { name: 'Сторона контроля', what: 'Насколько ты ограничиваешь себя в еде ради веса — и насколько это держится.' },
};

type Meal = { consumed_at: string; total_calories: number | null };

export default function PortraitFull() {
  const nav = useNavigate();
  const { profile } = useUserStore();
  const [meals, setMeals] = useState<Meal[] | null>(null);
  const paid = isPaidNow(profile);

  useEffect(() => {
    if (!profile?.id || !paid) return;
    const from = new Date(); from.setDate(from.getDate() - 14);
    supabase.from('meals').select('consumed_at,total_calories').eq('user_id', profile.id)
      .gte('consumed_at', from.toISOString())
      .then(({ data }) => setMeals((data as Meal[]) ?? []));
  }, [profile?.id, paid]);

  if (!profile) return null;
  const ep = profile.eating_profile;

  const header = (
    <>
      <button onClick={() => nav(-1)} className="flex items-center gap-1 text-sm text-ink-muted mb-4">
        <ChevronLeft size={18} /> Назад
      </button>
      <Mascot pose="support" className="w-24 h-24 mx-auto mb-3" />
      <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-glow text-center mb-2">Расширенный портрет</div>
    </>
  );

  if (!ep) {
    return (
      <div className="max-w-md mx-auto px-5 pt-4 pb-28">
        {header}
        <p className="text-sm text-ink-muted text-center mb-5">Портрет строится по тесту пищевого поведения.</p>
        <button onClick={() => nav('/test')} className="w-full py-3.5 rounded-xl bg-emerald-glow text-bg font-display font-bold">Пройти тест</button>
      </div>
    );
  }

  if (!paid) {
    return (
      <div className="max-w-md mx-auto px-5 pt-4 pb-28">
        {header}
        <h1 className="font-display text-2xl font-bold text-center text-balance mb-4">Твой портрет пищевого поведения</h1>
        <div className="card p-4 mb-5 relative overflow-hidden">
          <p className="text-sm text-ink leading-relaxed blur-[5px] select-none" aria-hidden>
            Все три стороны пищевого поведения, ситуации, в которых тебя тянет к еде, и твой ритм по дневнику за две недели — с первым шагом под тебя.
          </p>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="flex items-center gap-1.5 text-xs bg-bg-card/90 px-3 py-1.5 rounded-full border border-line"><Lock size={12} /> Бонус к подписке</span>
          </div>
        </div>
        <button onClick={() => nav('/paywall')} className="w-full py-3.5 rounded-xl bg-emerald-glow text-bg font-display font-bold">Открыть с подпиской</button>
      </div>
    );
  }

  const portrait = buildPortrait(ep, profile.sex as any);
  // Ритм по дневнику
  const days = new Map<string, { b15: number; a19: number; late: number }>();
  for (const m of meals ?? []) {
    const d = new Date(m.consumed_at);
    const key = d.toDateString();
    const r = days.get(key) ?? { b15: 0, a19: 0, late: 0 };
    const k = Number(m.total_calories) || 0;
    if (d.getHours() < 15) r.b15 += k;
    if (d.getHours() >= 19) r.a19 += k;
    if (d.getHours() >= 21) r.late += 1;
    days.set(key, r);
  }
  const n = days.size;
  const avg = (f: 'b15' | 'a19' | 'late') => n ? [...days.values()].reduce((a, r) => a + r[f], 0) / n : 0;

  return (
    <div className="max-w-md mx-auto px-5 pt-4 pb-28">
      {header}
      <h1 className="font-display text-2xl font-bold text-center text-balance mb-2">{portrait.title}</h1>
      {portrait.intensity && <p className="text-xs text-ink-dim text-center mb-4">{portrait.intensity}</p>}
      <div className="space-y-3 mb-6">
        {portrait.paragraphs.map((t, i) => <p key={i} className="text-[15px] text-ink leading-relaxed">{t}</p>)}
      </div>

      <div className="font-display text-lg font-bold mb-3">Три стороны</div>
      <div className="space-y-3 mb-6">
        {(['emotional', 'external', 'restrained'] as EatingScale[]).map((sc) => {
          const v = ep[sc];
          // Округляем до десятых: 2.3 − 1.8 в плавающей точке даёт 0.4999… и ведущий тип
          // подписывался бы «около обычного», противореча портрету выше.
          const diff = Math.round((v - NORMS[sc]) * 10) / 10;
          const sit = situations(ep, sc, 3);
          return (
            <div key={sc} className="card p-4">
              <div className="flex justify-between items-baseline mb-1">
                <span className="font-medium text-sm">{SCALE[sc].name}</span>
                <span className={`text-xs ${diff >= 0.5 ? 'text-emerald-glow' : 'text-ink-dim'}`}>
                  {v.toFixed(1)} · обычно {NORMS[sc].toFixed(1)}
                </span>
              </div>
              <p className="text-xs text-ink-muted leading-relaxed mb-2">{SCALE[sc].what}</p>
              <p className="text-sm text-ink leading-relaxed">
                {diff >= 0.5 ? 'Выше обычного — это твоя главная точка работы.'
                  : diff <= -0.5 ? 'Ниже обычного — здесь у тебя опора.'
                  : 'Около обычного — особого внимания не требует.'}
                {sit.length > 0 && (diff >= 0.5 ? ` Чаще всего: ${sit.join(', ')}.` : ` Бывает: ${sit.join(', ')}.`)}
              </p>
            </div>
          );
        })}
      </div>

      <div className="font-display text-lg font-bold mb-3">Твой ритм за две недели</div>
      <div className="card p-4 mb-6 text-sm leading-relaxed">
        {meals === null ? <span className="text-ink-muted">Собираю…</span>
          : n < 3 ? <span className="text-ink-muted">Нужно хотя бы 3 дня записей — тогда покажу, как распределяется еда по дню.</span>
          : (
            <div className="space-y-1.5 text-ink">
              <div>До 15:00 — в среднем <b>{Math.round(avg('b15'))} ккал</b></div>
              <div>После 19:00 — <b>{Math.round(avg('a19'))} ккал</b></div>
              <div>Приёмов после 21:00 — <b>{avg('late').toFixed(1).replace('.', ',')}</b> в день</div>
              <p className="text-xs text-ink-muted pt-1">
                {avg('a19') > avg('b15') * 1.5
                  ? 'Основная еда сдвинута на вечер. Чаще всего отсюда и вечерний голод: днём организм недополучает, вечером забирает своё.'
                  : 'Еда распределена по дню ровно — вечеру не приходится «добирать».'}
                {' '}Записи за {n} {n < 5 ? 'дня' : 'дней'}.
              </p>
            </div>
          )}
      </div>

      <div className="font-display text-lg font-bold mb-3">Первый шаг</div>
      <div className="rounded-2xl border border-emerald-glow/30 bg-emerald-glow/5 p-4 mb-4 text-sm text-ink leading-relaxed">
        {STYLE_STEPS[ep.style][0]}
      </div>
      <p className="text-xs text-ink-muted text-center mb-5">{DISCLAIMER}</p>
      <button onClick={() => nav('/chat')} className="w-full py-3.5 rounded-xl bg-emerald-glow text-bg font-display font-bold">Обсудить с Дофи</button>
    </div>
  );
}
